import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Search, ChevronDown, Pencil, Trash2, X } from 'lucide-react';
import { useTeam } from 'shared/context/teamContext';
import axiosInstance from 'shared/services/api/axiosInstance';
import { useAppDialog } from 'shared/context/AppDialogContext';
import BrandDropdown from 'shared/components/ui/BrandDropdown';
import {
  settingsInputClass,
  settingsSolidButtonClass,
  settingsIconButtonClass,
  settingsTableShellClass,
  settingsModalClass,
} from 'features/settings/components/settingsTheme';
import { BrandLoaderInline } from 'features/settings/components/settingsLoader';
import { trackClientEvent } from 'shared/utils/trodoEvents';
import teamService from 'shared/services/teamService';
import { resolveAvatarSrc } from 'shared/components/ui/Avatar';
import {
  tableHeaderRowClass,
  tableHeaderCellClass,
  tableRowClass,
  tableDataCellClass,
  activityNeutralButtonClass,
  activitySearchWrapperClass,
  activitySearchInputClass,
} from 'features/activity/styles/dataTableStyles';

const INK = 'var(--text-primary)';
const MUTED = 'var(--text-muted)';

const ROLE_OPTIONS = [
  { value: 'owner', label: 'Owner', description: 'Primary team owner. Can delete team and transfer ownership.' },
  { value: 'admin', label: 'Admin', description: 'Full team access: manages members, billing, integrations, and all settings.' },
  { value: 'developer', label: 'Developer', description: 'Configures integrations, IDE/MCP keys, issue thresholds and agents. No member or billing access.' },
  { value: 'member', label: 'Member', description: 'Full app access: view and use everything. Cannot configure settings or invite members.' },
  { value: 'billing', label: 'Billing', description: 'Strictly Settings → Billing. No access to the rest of the app.' }
];

const INVITE_ROLE_OPTIONS = ROLE_OPTIONS.filter((o) => o.value !== 'owner');

/** BrandDropdown shape — the role description rides along as a searchable hint. */
const ROLE_DROPDOWN_OPTIONS = INVITE_ROLE_OPTIONS.map((o) => ({
  value: o.value,
  label: o.label,
  hint: o.description,
}));

function roleSelectRenderValue(value) {
  const opt = ROLE_OPTIONS.find((o) => o.value === value);
  return opt ? opt.label : value;
}

function roleLabel(role) {
  const normalized = role === 'user' ? 'member' : role;
  const opt = ROLE_OPTIONS.find((o) => o.value === normalized);
  if (opt) return opt.label;
  if (!role) return 'Member';
  return String(role).charAt(0).toUpperCase() + String(role).slice(1);
}

function safeMemberRole(role) {
  let r = role || 'member';
  if (r === 'user') r = 'member';
  return ROLE_OPTIONS.some((o) => o.value === r) ? r : 'member';
}

function displayName(member) {
  if (member?.name && String(member.name).trim()) return String(member.name).trim();
  if (member?.email) return member.email.split('@')[0];
  return 'Member';
}

function displayNameInvite(invite) {
  const fn = invite?.firstName?.trim();
  const ln = invite?.lastName?.trim();
  if (fn || ln) return [fn, ln].filter(Boolean).join(' ');
  if (invite?.email) return invite.email.split('@')[0];
  return 'Invited';
}

function formatJoined(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatLastSeen(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} · ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
}

/**
 * A member's picture, or the coloured initial we have always drawn.
 *
 * The colour is derived from the name, so the same person keeps the same
 * bubble — worth preserving for the people who have no picture, and for
 * pending invites, which are an email address and nothing else.
 */
function MemberAvatar({ label, src }) {
  const [failed, setFailed] = useState(false);
  const ch = (label || '?').charAt(0).toUpperCase();
  const hue =
    typeof label === 'string'
      ? label.split('').reduce((a, c) => a + c.charCodeAt(0), 0) % 360
      : 0;
  const resolved = resolveAvatarSrc(src);
  if (resolved && !failed) {
    return (
      <img
        src={resolved}
        alt=""
        title={label || undefined}
        referrerPolicy="no-referrer"
        onError={() => setFailed(true)}
        className="h-6 w-6 shrink-0 rounded-full object-cover"
      />
    );
  }
  return (
    <div
      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-medium text-ink-onBrand"
      style={{ backgroundColor: `hsl(${hue} 52% 46%)` }}
    >
      {ch}
    </div>
  );
}

const MembersPage = () => {
  const { confirm: appConfirm } = useAppDialog();
  const { selectedTeam } = useTeam();

  const [inviteOpen, setInviteOpen] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('member');

  const [members, setMembers] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [inviteError, setInviteError] = useState(null);
  const [currentUserEmail, setCurrentUserEmail] = useState('');
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const [updatingRoles, setUpdatingRoles] = useState(new Set());
  const [deletingMembers, setDeletingMembers] = useState(new Set());
  const [manageAccessOpen, setManageAccessOpen] = useState(false);
  const [manageAccessEmail, setManageAccessEmail] = useState('');
  const [manageAccessRole, setManageAccessRole] = useState('member');

  const [searchQuery, setSearchQuery] = useState('');
  const [sortNameDesc, setSortNameDesc] = useState(false);
  const [openSections, setOpenSections] = useState({
    active: true,
    invited: false
  });

  useEffect(() => {
    try {
      const userString = localStorage.getItem('User');
      if (userString) {
        const userObj = JSON.parse(userString);
        setCurrentUserEmail(userObj.email || '');
      }
    } catch (err) {
      console.error('Error getting user email:', err);
    }
  }, []);

  const fetchPendingInvites = useCallback(async () => {
    if (!selectedTeam?.id && !selectedTeam?._id) return;
    try {
      const teamId = selectedTeam.id || selectedTeam._id;
      const response = await axiosInstance.get(`/team/${teamId}/pending-invites`);
      setPendingInvites(response.data.pendingInvites || []);
    } catch (err) {
      console.error('Error fetching pending invites:', err);
    }
  }, [selectedTeam]);

  const fetchMembers = useCallback(async () => {
    if (!selectedTeam?.id && !selectedTeam?._id) return;
    try {
      setLoading(true);
      const teamId = selectedTeam.id || selectedTeam._id;
      const response = await axiosInstance.get(`/team/${teamId}/members`);
      const membersList = response.data?.members || response.data || [];
      setMembers(membersList);

      const currentMember = membersList.find(
        (member) =>
          member.email?.toLowerCase() === currentUserEmail.toLowerCase() ||
          member.userId?.toString() === localStorage.getItem('userId')
      );

      if (currentMember) {
        const memberIndex = membersList.findIndex(
          (m) => m.email === currentMember.email || m.userId === currentMember.userId
        );
        if (memberIndex === 0) {
          setCurrentUserRole('owner');
        } else {
          const rr = currentMember.role || 'member';
          setCurrentUserRole(rr === 'user' ? 'member' : rr);
        }
      }
    } catch (err) {
      console.error('Error fetching members:', err);
      setError(err.response?.data?.message || 'Failed to fetch members');
    } finally {
      setLoading(false);
    }
  }, [selectedTeam, currentUserEmail]);

  useEffect(() => {
    if (selectedTeam?.id || selectedTeam?._id) {
      fetchMembers();
      fetchPendingInvites();
    }
  }, [selectedTeam, fetchMembers, fetchPendingInvites]);

  const resetInviteForm = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setRole('member');
    setInviteError(null);
  };

  const closeInvite = () => {
    setInviteOpen(false);
    resetInviteForm();
  };

  const handleInvite = async (e) => {
    e?.preventDefault();
    if (!email.trim()) {
      setInviteError('Email is required');
      return;
    }
    if (!selectedTeam?.id && !selectedTeam?._id) {
      setInviteError('No team selected');
      return;
    }
    if (currentUserRole !== 'owner' && currentUserRole !== 'admin') {
      setInviteError('Only an owner or admin can invite members');
      return;
    }

    try {
      setLoading(true);
      setInviteError(null);
      setError(null);
      setSuccess(null);

      const teamId = selectedTeam.id || selectedTeam._id;
      trackClientEvent(
        'member_invite_submitted',
        { invited_role: role, has_first_name: !!firstName.trim(), has_last_name: !!lastName.trim() },
        { category: 'workspace' }
      );
      const response = await axiosInstance.post('/team/invite', {
        email: email.trim(),
        role,
        teamId,
        firstName: firstName.trim() || undefined,
        lastName: lastName.trim() || undefined
      });

      if (response.data.addedImmediately) {
        setSuccess(`Successfully added ${response.data.user?.email || email.trim()} to the team.`);
      } else {
        setSuccess(`Invitation sent to ${email.trim()}. They will be added when they sign up.`);
      }

      closeInvite();
      await Promise.all([fetchMembers(), fetchPendingInvites()]);
    } catch (err) {
      console.error('Error inviting member:', err);
      setInviteError(err.response?.data?.message || err.response?.data?.error || 'Failed to invite member');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (memberEmail, newRole) => {
    if (!selectedTeam?.id && !selectedTeam?._id) return false;
    if (currentUserRole !== 'owner' && currentUserRole !== 'admin') {
      setError('Only an owner or admin can change roles');
      return false;
    }
    const memberIndex = members.findIndex((m) => m.email === memberEmail);
    if (memberIndex === 0) {
      setError('Cannot change owner role');
      return false;
    }
    try {
      setUpdatingRoles((prev) => new Set(prev).add(memberEmail));
      setError(null);
      setSuccess(null);
      const teamId = selectedTeam.id || selectedTeam._id;
      trackClientEvent(
        'member_role_change_submitted',
        { new_role: newRole },
        { category: 'workspace' }
      );
      await axiosInstance.post('/team/update-member-role', {
        teamId,
        email: memberEmail,
        newRole
      });
      // The cached team row is now stale — the next reader must go to the
      // server rather than be served a pre-change name/role for the session.
      teamService.invalidate(teamId, 'team');
      await fetchMembers();
      setSuccess('Role updated successfully');
      setTimeout(() => setSuccess(null), 3000);
      return true;
    } catch (err) {
      console.error('Error updating role:', err);
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to update role');
      return false;
    } finally {
      setUpdatingRoles((prev) => {
        const next = new Set(prev);
        next.delete(memberEmail);
        return next;
      });
    }
  };

  const openManageAccess = (memberEmail) => {
    const m = members.find((x) => x.email === memberEmail);
    setManageAccessEmail(memberEmail);
    setManageAccessRole(safeMemberRole(m?.role));
    setManageAccessOpen(true);
  };

  const handleSaveManageAccess = async () => {
    const m = members.find((x) => x.email === manageAccessEmail);
    const current = safeMemberRole(m?.role);
    if (manageAccessRole === current) {
      setManageAccessOpen(false);
      return;
    }
    const ok = await handleRoleChange(manageAccessEmail, manageAccessRole);
    if (ok) setManageAccessOpen(false);
  };

  const handleDeleteMember = async (memberEmail) => {
    if (!selectedTeam?.id && !selectedTeam?._id) return;
    if (currentUserRole !== 'owner' && currentUserRole !== 'admin') {
      setError('Only an owner or admin can remove members');
      return;
    }
    const member = members.find((m) => m.email === memberEmail);
    if (member && isOwner(member)) {
      setError('Cannot remove owner');
      return;
    }
    const okRemove = await appConfirm(`Are you sure you want to remove ${memberEmail} from the team?`, {
      title: 'Remove member',
      confirmLabel: 'Remove',
      danger: true
    });
    if (!okRemove) return;
    try {
      setDeletingMembers((prev) => new Set(prev).add(memberEmail));
      setError(null);
      const teamId = selectedTeam.id || selectedTeam._id;
      trackClientEvent('member_remove_submitted', {}, { category: 'workspace' });
      await axiosInstance.post('/team/remove-member', { teamId, email: memberEmail });
      // The cached team row is now stale — the next reader must go to the
      // server rather than be served a pre-change name/role for the session.
      teamService.invalidate(teamId, 'team');
      await fetchMembers();
      setSuccess('Member removed successfully');
    } catch (err) {
      console.error('Error removing member:', err);
      setError(err.response?.data?.message || 'Failed to remove member');
    } finally {
      setDeletingMembers((prev) => {
        const next = new Set(prev);
        next.delete(memberEmail);
        return next;
      });
    }
  };

  const handleCancelInvite = async (inviteEmail) => {
    if (!selectedTeam?.id && !selectedTeam?._id) return;
    if (currentUserRole !== 'owner' && currentUserRole !== 'admin') {
      setError('Only an owner or admin can cancel invitations');
      return;
    }
    const okCancel = await appConfirm(`Cancel invitation for ${inviteEmail}?`, {
      title: 'Cancel invitation',
      confirmLabel: 'Cancel invite'
    });
    if (!okCancel) return;
    try {
      setError(null);
      const teamId = selectedTeam.id || selectedTeam._id;
      trackClientEvent('member_invite_cancel_submitted', {}, { category: 'workspace' });
      await axiosInstance.post('/team/cancel-invite', { teamId, email: inviteEmail });
      await fetchPendingInvites();
      setSuccess('Invitation cancelled successfully');
    } catch (err) {
      console.error('Error cancelling invite:', err);
      setError(err.response?.data?.message || 'Failed to cancel invitation');
    }
  };

  const isOwner = (member) => {
    const idx = members.findIndex((m) => m.email === member.email || m.userId === member.userId);
    return idx === 0;
  };

  const isAdmin = currentUserRole === 'owner' || currentUserRole === 'admin';

  const q = searchQuery.trim().toLowerCase();
  const matchesMember = useCallback(
    (m) => {
      if (!q) return true;
      const name = displayName(m).toLowerCase();
      const em = (m.email || '').toLowerCase();
      return name.includes(q) || em.includes(q);
    },
    [q]
  );
  const matchesInvite = useCallback(
    (inv) => {
      if (!q) return true;
      const name = displayNameInvite(inv).toLowerCase();
      const em = (inv.email || '').toLowerCase();
      return name.includes(q) || em.includes(q);
    },
    [q]
  );

  const filteredMembers = useMemo(
    () => members.filter(matchesMember),
    [members, matchesMember]
  );
  const filteredInvites = useMemo(
    () => pendingInvites.filter(matchesInvite),
    [pendingInvites, matchesInvite]
  );

  const sortedMembers = useMemo(() => {
    const list = [...filteredMembers];
    list.sort((a, b) => {
      const an = displayName(a).toLowerCase();
      const bn = displayName(b).toLowerCase();
      const cmp = an.localeCompare(bn);
      return sortNameDesc ? -cmp : cmp;
    });
    return list;
  }, [filteredMembers, sortNameDesc]);

  const toggleSection = (key) => {
    setOpenSections((s) => ({ ...s, [key]: !s[key] }));
  };

  const tableHeadClass = tableHeaderCellClass;
  // Rows open nothing (actions live in the kebab), so no pointer.
  const memberRowClass = tableRowClass.replace('cursor-pointer ', '');

  const actionsCls = 'inline-flex gap-0.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100';

  const renderMemberRow = (member) => (
    <tr key={member.email} className={`${memberRowClass} group`}>
      <td className={tableDataCellClass}>
        <div className="flex min-w-0 items-center gap-2">
          <MemberAvatar label={displayName(member)} src={member.avatar_url} />
          <span className="truncate text-[12px] font-medium" style={{ color: INK }}>
            {displayName(member)}
          </span>
        </div>
      </td>
      <td className={`${tableDataCellClass} hidden text-[color:var(--text-muted)] sm:table-cell`}>
        <span className="break-all">{member.email}</span>
      </td>
      <td className={tableDataCellClass} style={{ color: MUTED }}>
        {updatingRoles.has(member.email) ? (
          'Updating…'
        ) : isOwner(member) ? (
          'Owner'
        ) : (
          roleSelectRenderValue(safeMemberRole(member.role))
        )}
      </td>
      <td className={`${tableDataCellClass} hidden text-[color:var(--text-muted)] md:table-cell`}>
        {formatJoined(member.createdAt ?? member.created_at)}
      </td>
      <td className={`${tableDataCellClass} hidden text-[color:var(--text-muted)] lg:table-cell`}>
        {formatLastSeen(member.updatedAt ?? member.updated_at)}
      </td>
      <td className={`${tableDataCellClass} w-1 whitespace-nowrap !px-2 !text-right`}>
        {isAdmin && !isOwner(member) && (
          <div className={actionsCls}>
            <button
              type="button"
              aria-label={`Manage access for ${member.email}`}
              title="Manage access"
              onClick={() => openManageAccess(member.email)}
              className={settingsIconButtonClass}
            >
              <Pencil size={13} aria-hidden />
            </button>
            <button
              type="button"
              aria-label={`Remove ${member.email}`}
              title="Remove from team"
              onClick={() => handleDeleteMember(member.email)}
              disabled={deletingMembers.has(member.email)}
              className={settingsIconButtonClass}
            >
              <Trash2 size={13} aria-hidden />
            </button>
          </div>
        )}
      </td>
    </tr>
  );

  const renderInviteRow = (invite, idx) => (
    <tr key={`inv-${invite.email}-${idx}`} className={`${memberRowClass} group`}>
      <td className={tableDataCellClass}>
        <div className="flex min-w-0 items-center gap-2">
          <MemberAvatar label={displayNameInvite(invite)} />
          <span className="truncate text-[12px] font-medium" style={{ color: INK }}>
            {displayNameInvite(invite)}
          </span>
        </div>
      </td>
      <td className={`${tableDataCellClass} hidden text-[color:var(--text-muted)] sm:table-cell`}>
        <span className="break-all">{invite.email}</span>
      </td>
      <td className={tableDataCellClass} style={{ color: MUTED }}>
        {roleLabel(invite.role)}
      </td>
      <td className={`${tableDataCellClass} hidden text-[color:var(--text-muted)] md:table-cell`}>
        {formatJoined(invite.invitedAt ?? invite.invited_at)}
      </td>
      <td className={`${tableDataCellClass} hidden text-[color:var(--text-muted)] lg:table-cell`}>Pending</td>
      <td className={`${tableDataCellClass} w-1 whitespace-nowrap !px-2 !text-right`}>
        {isAdmin && (
          <div className={actionsCls}>
            <button
              type="button"
              aria-label={`Cancel invitation for ${invite.email}`}
              title="Cancel invitation"
              onClick={() => handleCancelInvite(invite.email)}
              className={settingsIconButtonClass}
            >
              <X size={14} aria-hidden />
            </button>
          </div>
        )}
      </td>
    </tr>
  );

  const SectionHeaderRow = ({ id, label, count }) => (
    <tr>
      <td colSpan={6} className="border-b border-[color:var(--border-subtle)] p-0">
        <button
          type="button"
          onClick={() => toggleSection(id)}
          className="flex h-8 w-full items-center gap-2 px-3 text-left transition-colors hover:bg-surface-hover"
        >
          <ChevronDown
            className={`h-4 w-4 shrink-0 text-[color:var(--text-muted)] transition-transform ${openSections[id] ? '' : '-rotate-90'}`}
            strokeWidth={2}
          />
          <span className="text-[12px] font-medium" style={{ color: INK }}>
            {label} <span className="text-[color:var(--text-muted)]">({count})</span>
          </span>
        </button>
      </td>
    </tr>
  );

  const modalShell = (onClose, children, labelId) => (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ background: 'var(--surface-overlay)' }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelId}
        className={`${settingsModalClass} max-w-md`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );

  const labelCls = 'mb-1 block text-[12px] text-[color:var(--text-muted)]';

  return (
    <div className="w-full pb-6 text-left" style={{ fontFamily: "'Inter', sans-serif" }}>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <label className={`${activitySearchWrapperClass} w-full max-w-xs`}>
          <Search size={13} className="shrink-0 text-[color:var(--text-muted)]" aria-hidden />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search members"
            className={activitySearchInputClass}
            aria-label="Search members"
          />
        </label>
        {isAdmin && (
          <button
            type="button"
            onClick={() => {
              setInviteOpen(true);
              setInviteError(null);
            }}
            className={`${settingsSolidButtonClass} ml-auto`}
          >
            Invite members
          </button>
        )}
      </div>

      {error && <p role="alert" className="mb-3 text-[12px] text-danger">{error}</p>}
      {success && <p role="status" className="mb-3 text-[12px] text-success">{success}</p>}

      {loading && members.length === 0 ? (
        <BrandLoaderInline label="Loading members" />
      ) : (
      <div className={`${settingsTableShellClass} w-full`}>
        <div className="w-full overflow-x-auto">
          <table className="w-full min-w-[640px] border-collapse text-left">
            <thead>
              <tr className={tableHeaderRowClass}>
                <th className={tableHeadClass}>
                  <button
                    type="button"
                    onClick={() => setSortNameDesc((d) => !d)}
                    className="inline-flex items-center gap-1 font-inherit text-inherit hover:text-[color:var(--text-primary)]"
                  >
                    Name
                    <span className="text-[9px] opacity-70">{sortNameDesc ? '↓' : '↑'}</span>
                  </button>
                </th>
                <th className={`${tableHeadClass} hidden sm:table-cell`}>Email</th>
                <th className={tableHeadClass}>Role</th>
                <th className={`${tableHeadClass} hidden md:table-cell`}>Joined</th>
                <th className={`${tableHeadClass} hidden lg:table-cell`}>Last seen</th>
                <th className={`${tableHeadClass} w-1`} aria-hidden />
              </tr>
            </thead>
            <tbody>
              <SectionHeaderRow id="active" label="Active" count={filteredMembers.length} />
              {openSections.active &&
                (sortedMembers.length > 0 ? (
                  sortedMembers.map(renderMemberRow)
                ) : (
                  <tr>
                    <td colSpan={6} className="px-3 py-6 text-center text-[12px]" style={{ color: MUTED }}>
                      {members.length > 0 ? 'No matches' : 'No members'}
                    </td>
                  </tr>
                ))}
              <SectionHeaderRow id="invited" label="Invited" count={filteredInvites.length} />
              {openSections.invited &&
                (filteredInvites.length > 0 ? (
                  filteredInvites.map(renderInviteRow)
                ) : (
                  <tr>
                    <td colSpan={6} className="px-3 py-6 text-center text-[12px]" style={{ color: MUTED }}>
                      No pending invitations
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {inviteOpen && modalShell(() => { if (!loading) closeInvite(); }, (
        <form onSubmit={handleInvite}>
          <div className="flex items-center justify-between border-b border-[color:var(--border-default)] px-4 py-3">
            <h2 id="invite-title" className="text-[14px] font-semibold text-[color:var(--text-primary)]">Invite member</h2>
            <button type="button" onClick={closeInvite} disabled={loading} className={settingsIconButtonClass} aria-label="Close">
              <X size={14} aria-hidden />
            </button>
          </div>
          <div className="px-4 py-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label htmlFor="invite-fn" className={labelCls}>First name</label>
                <input
                  id="invite-fn"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className={settingsInputClass}
                  autoComplete="given-name"
                />
              </div>
              <div>
                <label htmlFor="invite-ln" className={labelCls}>Last name</label>
                <input
                  id="invite-ln"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className={settingsInputClass}
                  autoComplete="family-name"
                />
              </div>
            </div>
            <div className="mt-3">
              <label htmlFor="invite-email" className={labelCls}>Email</label>
              <input
                id="invite-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@company.com"
                className={settingsInputClass}
                autoComplete="email"
              />
            </div>
            <div className="mt-3">
              {/* A <span>, not a <label>: BrandDropdown's trigger is a button,
                  which htmlFor can't target: it takes ariaLabel instead. */}
              <span className={labelCls}>Role</span>
              <BrandDropdown
                value={role}
                onChange={setRole}
                options={ROLE_DROPDOWN_OPTIONS}
                placeholder="Select a role"
                ariaLabel="Role for the invited member"
              />
            </div>
            {inviteError && (
              <p className="mt-3 text-[12px] text-danger" role="alert">{inviteError}</p>
            )}
          </div>
          <div className="flex justify-end gap-2 px-4 pb-4">
            <button type="button" onClick={closeInvite} disabled={loading} className={activityNeutralButtonClass}>
              Cancel
            </button>
            <button type="submit" disabled={loading || !email.trim()} className={settingsSolidButtonClass}>
              {loading ? 'Sending…' : 'Send invitation'}
            </button>
          </div>
        </form>
      ), 'invite-title')}

      {manageAccessOpen && modalShell(() => setManageAccessOpen(false), (
        <div className="p-4">
          <h2 id="manage-title" className="text-[14px] font-semibold text-[color:var(--text-primary)]">Manage access</h2>
          <p className="mb-3 mt-0.5 truncate text-[12px] text-[color:var(--text-muted)]">{manageAccessEmail}</p>
          <BrandDropdown
            value={manageAccessRole}
            onChange={setManageAccessRole}
            options={ROLE_DROPDOWN_OPTIONS}
            placeholder="Select a role"
            ariaLabel="Role for this member"
          />
          <div className="mt-4 flex justify-end gap-2">
            <button type="button" onClick={() => setManageAccessOpen(false)} className={activityNeutralButtonClass}>
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSaveManageAccess}
              disabled={updatingRoles.has(manageAccessEmail)}
              className={settingsSolidButtonClass}
            >
              Save
            </button>
          </div>
        </div>
      ), 'manage-title')}
    </div>
  );
};

export default MembersPage;
